/**
 * Aba Empresa — dados cadastrais + endereço + contato.
 *
 * Auto-preenchimento de endereço via ViaCEP no blur do CEP.
 * Validação client-side de CNPJ.
 * Bloqueio de CNPJ/Razão Social pra tenant_admin (campos read-only com tooltip).
 */

import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Search } from "lucide-react";
import toast from "react-hot-toast";

import { tenantsCompanyApi } from "@/api/tenantsCompany";
import { apiErrorMessage } from "@/api/client";
import { qk, useCepLookup, useTenantCompany } from "@/hooks/queries";
import { useAuth } from "@/contexts/AuthContext";
import type { TenantCompanyUpdate, TipoEstabelecimento } from "@/types";

import { Button } from "@/components/Button";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { Field, Input, Select } from "@/components/Field";
import { Skeleton } from "@/components/Skeleton";
import { Tip } from "@/components/Tip";

const TIPO_OPTIONS: { value: TipoEstabelecimento; label: string }[] = [
  { value: "laboratorio",      label: "Laboratório" },
  { value: "farmacia",         label: "Farmácia" },
  { value: "hospital",         label: "Hospital" },
  { value: "clinica",          label: "Clínica" },
  { value: "distribuidora",    label: "Distribuidora" },
  { value: "transportadora",   label: "Transportadora" },
  { value: "industria_farma",  label: "Indústria Farmacêutica" },
  { value: "banco_sangue",     label: "Banco de Sangue" },
  { value: "outro",            label: "Outro" },
];

const UF_LIST = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA",
  "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN",
  "RS", "RO", "RR", "SC", "SP", "SE", "TO",
];

export function CompanyTab({ tenantId }: { tenantId: string }) {
  const auth = useAuth();
  const company = useTenantCompany(tenantId);
  const cep = useCepLookup();
  const qc = useQueryClient();

  const [form, setForm] = useState<TenantCompanyUpdate>({});
  const [cepLoading, setCepLoading] = useState(false);

  // Inicializa o form quando company chega
  useEffect(() => {
    if (company.data) setForm({});
  }, [company.data?.tenant_id]);

  const mut = useMutation({
    mutationFn: (p: TenantCompanyUpdate) => tenantsCompanyApi.updateCompany(tenantId, p),
    onSuccess: () => {
      toast.success("Cadastro atualizado");
      void qc.invalidateQueries({ queryKey: qk.tenantCompany(tenantId) });
      void qc.invalidateQueries({ queryKey: qk.tenantCompleteness(tenantId) });
      setForm({});
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  if (company.isLoading) return <Skeleton className="h-96 w-full" />;
  if (!company.data) return <p className="text-sm text-slate-500 dark:text-slate-400">Sem dados.</p>;

  const c = company.data;

  // Valor exibido = override do form (se editado) || valor do banco
  const v = <K extends keyof TenantCompanyUpdate>(k: K): string => {
    const fv = form[k];
    if (fv !== undefined) return (fv as string) ?? "";
    const cv = c[k as keyof typeof c];
    return (cv as string) ?? "";
  };

  // Helper pra setar campo no form override
  const set = (k: keyof TenantCompanyUpdate, val: string | null) => {
    setForm((f) => ({ ...f, [k]: val === "" ? null : val }));
  };

  const canEditSensitive = auth.isSuperAdmin;

  // Auto-CEP no blur
  async function onCepBlur() {
    const raw = v("endereco_cep").replace(/\D/g, "");
    if (raw.length !== 8) return;
    setCepLoading(true);
    try {
      const result = await cep.lookup(raw);
      setForm((f) => ({
        ...f,
        endereco_cep: raw,
        // Só sobrescreve se o usuário ainda não preencheu manualmente
        endereco_logradouro: f.endereco_logradouro ?? result.logradouro ?? c.endereco_logradouro ?? null,
        endereco_bairro:     f.endereco_bairro     ?? result.bairro     ?? c.endereco_bairro     ?? null,
        endereco_cidade:     f.endereco_cidade     ?? result.cidade     ?? c.endereco_cidade     ?? null,
        endereco_uf:         f.endereco_uf         ?? result.uf         ?? c.endereco_uf         ?? null,
      }));
      toast.success("Endereço preenchido");
    } catch {
      toast.error("CEP não encontrado");
    } finally {
      setCepLoading(false);
    }
  }

  const dirty = Object.keys(form).length > 0;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!dirty) return;
    mut.mutate(form);
  }

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {/* ---------- Empresa ---------- */}
      <Card>
        <CardHeader title="Dados da Empresa" />
        <CardBody className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Razão Social *"
              hint={!canEditSensitive ? "Apenas super_admin pode editar" : undefined}
            >
              <Input
                value={v("razao_social")}
                onChange={(e) => set("razao_social", e.target.value)}
                disabled={!canEditSensitive}
                required
              />
            </Field>
            <Field label="Nome Fantasia">
              <Input
                value={v("nome_fantasia")}
                onChange={(e) => set("nome_fantasia", e.target.value)}
              />
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field
              label="CNPJ *"
              hint={!canEditSensitive ? "Apenas super_admin" : "14 dígitos, com ou sem máscara"}
            >
              <Input
                value={formatCnpjDisplay(v("cnpj"))}
                onChange={(e) => set("cnpj", e.target.value.replace(/\D/g, ""))}
                disabled={!canEditSensitive}
                placeholder="00.000.000/0000-00"
                maxLength={18}
              />
            </Field>
            <Field label="Inscrição Estadual">
              <Input
                value={v("inscricao_estadual")}
                onChange={(e) => set("inscricao_estadual", e.target.value)}
                placeholder="ISENTO, se for o caso"
              />
            </Field>
            <Field label="Inscrição Municipal">
              <Input
                value={v("inscricao_municipal")}
                onChange={(e) => set("inscricao_municipal", e.target.value)}
              />
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Tipo de Estabelecimento *">
              <Select
                value={v("tipo_estabelecimento") || ""}
                onChange={(e) => set("tipo_estabelecimento", e.target.value || null)}
                options={[{ value: "", label: "Selecione..." }, ...TIPO_OPTIONS]}
                required
              />
            </Field>
            <Field label="CNAE Principal">
              <Input
                value={v("cnae_principal")}
                onChange={(e) => set("cnae_principal", e.target.value)}
                placeholder="0000-0/00"
              />
            </Field>
          </div>
        </CardBody>
      </Card>

      {/* ---------- Endereço ---------- */}
      <Card>
        <CardHeader title="Endereço" />
        <CardBody className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field
              label={
              <>
                CEP *
                <Tip label="CEP *">
                  Ao preencher, o endereço é completado automaticamente —
                  logradouro, bairro, cidade e UF.
                </Tip>
              </>
            }
            >
              <div className="relative">
                <Input
                  value={formatCepDisplay(v("endereco_cep"))}
                  onChange={(e) => set("endereco_cep", e.target.value.replace(/\D/g, ""))}
                  onBlur={onCepBlur}
                  placeholder="00000-000"
                  maxLength={9}
                />
                {cepLoading && (
                  <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-400 dark:text-slate-500" />
                )}
                {!cepLoading && v("endereco_cep") && (
                  <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-300" />
                )}
              </div>
            </Field>
            <Field label="Logradouro *" className="sm:col-span-2">
              <Input
                value={v("endereco_logradouro")}
                onChange={(e) => set("endereco_logradouro", e.target.value)}
                placeholder="Av., Rua, Travessa..."
              />
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-4">
            <Field label="Número *">
              <Input
                value={v("endereco_numero")}
                onChange={(e) => set("endereco_numero", e.target.value)}
              />
            </Field>
            <Field label="Complemento" className="sm:col-span-2">
              <Input
                value={v("endereco_complemento")}
                onChange={(e) => set("endereco_complemento", e.target.value)}
                placeholder="Sala, andar, bloco..."
              />
            </Field>
            <Field label="Bairro">
              <Input
                value={v("endereco_bairro")}
                onChange={(e) => set("endereco_bairro", e.target.value)}
              />
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Cidade *" className="sm:col-span-2">
              <Input
                value={v("endereco_cidade")}
                onChange={(e) => set("endereco_cidade", e.target.value)}
              />
            </Field>
            <Field label="UF *">
              <Select
                value={v("endereco_uf") || ""}
                onChange={(e) => set("endereco_uf", e.target.value || null)}
                options={[
                  { value: "", label: "—" },
                  ...UF_LIST.map((u) => ({ value: u, label: u })),
                ]}
              />
            </Field>
          </div>
        </CardBody>
      </Card>

      {/* ---------- Contato ---------- */}
      <Card>
        <CardHeader title="Contato Comercial" />
        <CardBody className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Telefone *">
              <Input
                type="tel"
                value={v("telefone")}
                onChange={(e) => set("telefone", e.target.value)}
                placeholder="+55 11 99999-9999"
              />
            </Field>
            <Field label="E-mail Corporativo *">
              <Input
                type="email"
                value={v("email")}
                onChange={(e) => set("email", e.target.value)}
                placeholder="contato@empresa.com.br"
              />
            </Field>
          </div>
          <Field label="Site (opcional)">
            <Input
              type="text"
              value={v("site")}
              onChange={(e) => set("site", e.target.value)}
              placeholder="empresa.com.br"
              pattern="\S*\.\S+"
              title="Domínio sem espaços, contendo pelo menos um ponto"
            />
          </Field>
        </CardBody>
      </Card>

      {/* ---------- Submit ---------- */}
      <div className="sticky bottom-4 flex items-center justify-end gap-3 rounded-xl border border-surface-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 p-4 shadow-card backdrop-blur">
        {dirty && (
          <span className="text-xs text-slate-500 dark:text-slate-400">
            Você tem alterações não salvas
          </span>
        )}
        <Button
          type="button"
          variant="secondary"
          onClick={() => setForm({})}
          disabled={!dirty}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          loading={mut.isPending}
          disabled={!dirty}
        >
          Salvar alterações
        </Button>
      </div>
    </form>
  );
}

// ---------- Helpers de formatação display ----------

function formatCnpjDisplay(raw: string): string {
  const d = (raw || "").replace(/\D/g, "");
  if (d.length !== 14) return raw || "";
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

function formatCepDisplay(raw: string): string {
  const d = (raw || "").replace(/\D/g, "");
  if (d.length !== 8) return raw || "";
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}
